import { i18n } from 'i18n'
import { opt } from 'store/opt'
import { store } from 'store/store'
import { BodyThickness, Gender, IdentityMode, ShapeType } from 'type/Identity'
import { Item } from 'type/Item'
import { OfferItem } from 'type/rec'
import { omit } from 'util/object'

export type InferenceStatus =
  | 'unknown'
  | 'accepted'
  | 'queued'
  | 'started'
  | 'working'
  | 'complete'
  | 'error'

export const LIKE = 1
export const DELETE = 2
export const DISLIKE = 3

const LIKE_MODES = [LIKE, DELETE, DISLIKE] as const

export type LikeMode = 0 | typeof LIKE_MODES[number]

export type ItemData = {
  article: string | undefined
  brand: string | undefined
  collection?: string
  color?: string
  description?: string
  dress_gender: string | undefined
  dress_shape_type: string | undefined
  external_item_id: string | undefined
  item?: string
  item_combo_image: string | undefined
  item_full_image: string | undefined
  links: string | undefined
  links_name: string | undefined
  product: string | undefined
  clothing_sizes?: string
}

export type TagData = {
  identity_id?: string
  identity?: {
    identity_mode?: IdentityMode
    gender?: Gender
    face1_url?: string
    user_url?: string
    dimensions?: {
      hips?: number
      chest?: number
      waist?: number
      height?: number
      shoulders?: number
      legLength?: number
    }
    extended_shape?: {
      height?: string
      legLength?: number
      shape_type?: ShapeType
      body_thickness: BodyThickness
    },
    shape_dimensions?: {
      hips?: number[]
      chest?: number[]
      waist?: number[]
      height?: number[]
      legLength?: number[]
    }
  }
  custom_collection_data?: ItemData
  items?: ItemData[]
}

export type ShowoffTags = {
  data?: TagData
}

export type InferenceImage = {
  created_at: string
  image_id: number
  image_url: string
  video_url?: string // только у видео, при is_video: 1
  is_video?: 0 | 1
  like_mode?: LikeMode
  showoff_tags?: ShowoffTags
}

export type Inference = {
  inference_id: string
  generated?: InferenceImage[]
  status: InferenceStatus
  job_name?: string
  is_viewed?: number // приходит только в списке GET /api/inferences
  user_url?: string // приходит только в списке GET /api/inferences
}

export type GenerateRequest = {
  user_id: string
  manager_id?: string
  identity_id: string
  item_id: string
  combo_item_id?: string
  country_code: string
  user_email?: string
  user_ext_id?: string
  item_ext_id?: string
  high?: boolean
  vendor?: string
  gemini_model?: string
  combo_generation?: boolean
}

export type InferenceResponse = {
  inference_id: string
  status: InferenceStatus
}

export type ImageInferenceResponse = InferenceResponse

export type VideoInferenceResponse = InferenceResponse

export function fromItem(it: Item | undefined): ItemData | undefined {
  if (!it) return undefined

  const {
    article,
    brand_name,
    collection_id,
    color,
    description,
    dress_gender,
    dress_shape_type,
    external_item_id,
    item_full_image,
    item_id,
    item_combo_image,
    links,
    links_name,
    product_name,
  } = it

  return {
    article,
    brand: brand_name,
    collection: collection_id,
    color,
    description,
    dress_gender,
    dress_shape_type,
    external_item_id,
    item: item_id,
    item_combo_image,
    item_full_image,
    links,
    links_name,
    product: product_name,
  }
}

export function fromOfferItem(it: OfferItem | undefined): ItemData | undefined {
  if (!it) return undefined

  const {
    articul,
    brand_name,
    color,
    description,
    dress_gender,
    dress_shape_type,
    external_item_id,
    item_id,
    item_combo_image,
    item_full_image,
    links,
    links_name,
    product_name,
  } = it

  return {
    article: articul,
    brand: brand_name,
    color,
    collection: undefined,
    description,
    dress_gender,
    dress_shape_type,
    external_item_id,
    item: item_id,
    item_combo_image,
    item_full_image,
    links,
    links_name,
    product: product_name,
  }
}

export function buildBuyLink(links?: string, inference_id?: string): string | undefined {
  links = links?.trim()
  if (!links) return

  try {
    const url = new URL(links)
    if (inference_id) url.searchParams.append('inference_id', inference_id)
    const utm = new URLSearchParams(opt.utm_buy)
    for (const [name, value] of utm) {
      url.searchParams.append(name, value)
    }
    return url.toString()
  } catch (e) {
    console.error(e)
    return links
  }
}

// Ссылку шерят в мессенджеры, где она печатается целиком, поэтому держим её короткой.
// Продукт ищется по префиксу product_id, восьми символов для этого достаточно.
const SHORT_PRODUCT_LENGTH = 8

function shortProduct(product: string | undefined): string | undefined {
  const id = product?.trim()
  if (!id) return undefined
  return id.substring(0, SHORT_PRODUCT_LENGTH)
}

function buildLink(path: string, inference_id: string, back?: string) {
  const product = shortProduct(opt.options.product)
  const params = new URLSearchParams(omit({ product, back }))
  return `${opt.share_host}/${path}/${inference_id}?${params}`
}

// back ведёт кнопку «В каталог» назад в магазин. Со стенда возвращаться некуда.
function buildBack(): string | undefined {
  return opt.stand ? undefined : store.meta?.location
}

// Ссылка для мессенджеров: краулеру достаётся превью, человека уводит на карточку товара.
export function buildShareLink(inference_id: string, links?: string) {
  // При наличии links человек уходит прямо в магазин и страницы примерки не видит,
  // поэтому back там незачем — он нужен только когда /shop уводит на /tryon.
  const back = links?.trim() ? undefined : buildBack()
  return buildLink('shop', inference_id, back)
}

// QR сканируют рядом с экраном — ведём на страницу просмотра примерки.
export function buildViewLink(inference_id: string) {
  return buildLink('tryon', inference_id, buildBack())
}

export function buildShareText(_name: string | undefined, _brand: string | undefined, _gender: string | undefined): string {
  return i18n('share.Text') as string
}

export function getShapeLabel(dress_shape_type: string | undefined): string | undefined {
  if (dress_shape_type === 'top') return i18n('DressShapeTop') as string
  if (dress_shape_type === 'bottom') return i18n('DressShapeBottom') as string
  return undefined
}

export function getItemShapeLabel(item: ItemData | undefined): string | undefined {
  return getShapeLabel(item?.dress_shape_type)
}
